import { useState, useCallback } from 'react';
import { DesktopNotificationDetails } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useQueryClient } from 'react-query';
import { TaskCenterTypes, TaskCenterQueries } from '@frontend/api-task-center';
import { UsersQueries } from '@frontend/api-users';
import { useScopedAppFlagStore } from '@frontend/scope';
import { SelectedFilters, TaskCenterContext, TaskMode } from '@frontend/task-center-scope';
import { TaskCenterTabs } from '../constants';
import { useTaskCenterFilters } from '../hooks';
import { useGetFilteredLocations } from './use-get-filtered-locations';

export const useGetContextValues = (): TaskCenterContext => {
  const queryClient = useQueryClient();
  const { getFeatureFlagValue, getCustomizationFlagValue } = useScopedAppFlagStore();

  // Static values
  const [taskMode, setTaskMode] = useState<TaskMode>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<(typeof TaskCenterTabs)[number]['controller']>(
    TaskCenterTabs[0].controller
  );
  const [selectedTask, setSelectedTask] = useState<TaskCenterTypes.Task | null>(null);

  const [isTaskCenterOpen, setIsTaskCenterOpen] = useState<boolean>(false);
  const [isFromContactPanel, setIsFromContactPanel] = useState<boolean>(false);

  // Intentionally using it like this
  const locationsData = useGetFilteredLocations();

  const locationIds = locationsData.map((loc) => loc.id);

  const {
    assigneeFilter,
    selectedFilters,
    setAssigneeFilter,
    setSelectedFilters,
    resetFilters,
    resetCreator,
    resetSelectedFilters,
  } = useTaskCenterFilters(
    'assigneeFilter',
    'setAssigneeFilter',
    'selectedFilters',
    'setSelectedFilters',
    'resetFilters',
    'resetCreator',
    'resetSelectedFilters'
  );

  // Dynamic values for the context as per the data fetched, sorting and filters
  const { data: assignees } = UsersQueries.useGetUsersInLocations(locationIds);
  const isAIRActive =
    getCustomizationFlagValue(Feature.TRUELARK_AI_VOICE) ||
    getCustomizationFlagValue(Feature.TRUELARK_AI_MESSAGING) ||
    getCustomizationFlagValue(Feature.CALL_INTELLIGENCE);
  const shouldGenerateAiTitle = getFeatureFlagValue('task-center-generate-ai-title');
  const isAIRCreatorEnabled = isAIRActive && getFeatureFlagValue('task-center-show-air-creator');

  // Setting the values for the context
  const setFilters = useCallback((values: SelectedFilters) => setSelectedFilters(values), [setSelectedFilters]);
  const setTab = useCallback(
    (tab: (typeof TaskCenterTabs)[number]['controller']) => setSelectedTab(tab),
    [setSelectedTab]
  );

  const onViewNotification = (notificationPayload: DesktopNotificationDetails['taskAlert']) => {
    if (notificationPayload?.taskId) {
      if (notificationPayload.taskId === taskId) {
        queryClient.removeQueries({
          queryKey: TaskCenterQueries.queryEndpointKeys.taskById,
        });
      }
      if (!notificationPayload?.body || notificationPayload?.notificationType?.includes('Task Updated')) {
        setTaskId?.(notificationPayload?.taskId ?? '');
        setTaskMode?.('edit');
      } else {
        if (selectedFilters.sortType !== TaskCenterTypes.SortField.SORT_DUE_DATE)
          setSelectedFilters({ ...selectedFilters, sortType: TaskCenterTypes.SortField.SORT_DUE_DATE });
      }
    }
    // Open task center in all cases
    setIsTaskCenterOpen(true);
  };

  return {
    taskMode,
    setTaskMode,
    isFromContactPanel,
    setIsFromContactPanel,
    selectedTask,
    setSelectedTask,
    locationIds,
    assignees: assignees?.users ?? [],
    locationsData,
    selectedFilters,
    setFilters,
    selectedTab,
    setTab,
    taskId,
    setTaskId,
    setAssigneeFilter,
    assigneeFilter,
    isTaskCenterOpen,
    setIsTaskCenterOpen,
    resetFilters,
    onViewNotification,
    resetCreator,
    resetSelectedFilters,
    shouldGenerateAiTitle,
    isAIRCreatorEnabled,
    highlightedTaskId,
    setHighlightedTaskId,
  };
};
